import React, { useState, useEffect } from 'react';
import { Trophy, RotateCcw, Star, Calculator } from 'lucide-react';
import { playSound } from '../../utils/soundManager';

const MathPuzzle = ({ onComplete, difficulty = 'easy' }) => {
  const [currentPuzzle, setCurrentPuzzle] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [gameWon, setGameWon] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const totalQuestions = 10;

  useEffect(() => {
    generatePuzzle();
  }, [difficulty]);

  useEffect(() => {
    let interval;
    if (isPlaying && !gameWon) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameWon]);

  const generatePuzzle = () => {
    let puzzle;
    
    switch (difficulty) {
      case 'easy':
        puzzle = generateEasyPuzzle();
        break;
      case 'medium':
        puzzle = generateMediumPuzzle();
        break;
      case 'hard':
        puzzle = generateHardPuzzle();
        break;
      default:
        puzzle = generateEasyPuzzle();
    }
    
    setCurrentPuzzle(puzzle);
    setUserAnswer('');
    setFeedback('');
    if (!isPlaying) setIsPlaying(true);
  };

  const generateEasyPuzzle = () => {
    const operations = ['+', '-'];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    
    let num1, num2, answer;
    
    if (operation === '+') {
      num1 = Math.floor(Math.random() * 20) + 1;
      num2 = Math.floor(Math.random() * 20) + 1;
      answer = num1 + num2;
    } else {
      num1 = Math.floor(Math.random() * 20) + 10;
      num2 = Math.floor(Math.random() * num1) + 1;
      answer = num1 - num2;
    }
    
    return {
      question: `${num1} ${operation} ${num2} = ?`,
      answer: answer,
      type: 'basic'
    };
  };

  const generateMediumPuzzle = () => {
    const puzzleTypes = ['multiplication', 'division', 'twoStep'];
    const type = puzzleTypes[Math.floor(Math.random() * puzzleTypes.length)];
    
    let num1, num2, answer, question;
    
    if (type === 'multiplication') {
      num1 = Math.floor(Math.random() * 10) + 2;
      num2 = Math.floor(Math.random() * 10) + 2;
      answer = num1 * num2;
      question = `${num1} × ${num2} = ?`;
    } else if (type === 'division') {
      num2 = Math.floor(Math.random() * 9) + 2;
      answer = Math.floor(Math.random() * 10) + 2;
      num1 = num2 * answer;
      question = `${num1} ÷ ${num2} = ?`;
    } else {
      num1 = Math.floor(Math.random() * 10) + 1;
      num2 = Math.floor(Math.random() * 10) + 1;
      const num3 = Math.floor(Math.random() * 10) + 1;
      answer = (num1 + num2) - num3;
      question = `${num1} + ${num2} - ${num3} = ?`;
    }
    
    return { question, answer, type };
  };

  const generateHardPuzzle = () => {
    const puzzleTypes = ['complex', 'pattern', 'missing'];
    const type = puzzleTypes[Math.floor(Math.random() * puzzleTypes.length)];
    
    let question, answer;
    
    if (type === 'complex') {
      const num1 = Math.floor(Math.random() * 10) + 2;
      const num2 = Math.floor(Math.random() * 5) + 2;
      const num3 = Math.floor(Math.random() * 10) + 1;
      answer = (num1 * num2) + num3;
      question = `(${num1} × ${num2}) + ${num3} = ?`;
    } else if (type === 'pattern') {
      const start = Math.floor(Math.random() * 10) + 1;
      const step = Math.floor(Math.random() * 5) + 2;
      const sequence = [start, start + step, start + (step * 2), start + (step * 3)];
      answer = start + (step * 4);
      question = `${sequence.join(', ')}, ?`;
    } else {
      const num1 = Math.floor(Math.random() * 15) + 5;
      const num2 = Math.floor(Math.random() * 10) + 2;
      answer = num2;
      const result = num1 + num2;
      question = `${num1} + ? = ${result}`;
    }
    
    return { question, answer, type };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const userNum = parseInt(userAnswer);
    
    if (isNaN(userNum)) {
      setFeedback('⚠️ Please enter a number!');
      setTimeout(() => setFeedback(''), 1500);
      return;
    }
    
    if (userNum === currentPuzzle.answer) {
      playSound('correct');
      setFeedback('✅ Correct!');
      setScore(score + 1);
      
      setTimeout(() => {
        const newQuestionsAnswered = questionsAnswered + 1;
        setQuestionsAnswered(newQuestionsAnswered);
        
        if (newQuestionsAnswered >= totalQuestions) {
          setGameWon(true);
          setIsPlaying(false);
          if (onComplete) {
            onComplete({ 
              completed: true,
              score: score + 1, 
              total: totalQuestions,
              time: timer,
              percentage: Math.round(((score + 1) / totalQuestions) * 100)
            });
          }
        } else {
          generatePuzzle();
        }
      }, 1000);
    } else {
      playSound('incorrect');
      setFeedback(`❌ Wrong! Answer is ${currentPuzzle.answer}`);
      
      setTimeout(() => {
        const newQuestionsAnswered = questionsAnswered + 1;
        setQuestionsAnswered(newQuestionsAnswered);
        
        if (newQuestionsAnswered >= totalQuestions) {
          setGameWon(true);
          setIsPlaying(false);
          if (onComplete) {
            onComplete({ 
              completed: true,
              score, 
              total: totalQuestions,
              time: timer,
              percentage: Math.round((score / totalQuestions) * 100)
            });
          }
        } else {
          generatePuzzle();
        }
      }, 2000);
    }
  };

  const handleReset = () => {
    playSound('click');
    setScore(0);
    setQuestionsAnswered(0);
    setTimer(0);
    setGameWon(false);
    setIsPlaying(false);
    generatePuzzle();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!currentPuzzle) return null;

  return (
    <div className="math-puzzle">
      <div className="game-header">
        <div className="game-stats">
          <div className="stat-item">
            <span className="stat-label">Score:</span>
            <span className="stat-value">{score}/{totalQuestions}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Question:</span>
            <span className="stat-value">{questionsAnswered + 1}/{totalQuestions}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Time:</span>
            <span className="stat-value">{formatTime(timer)}</span>
          </div>
        </div>
        
        <button 
          className="reset-button hover-lift"
          onClick={handleReset}
        >
          <RotateCcw size={20} />
          Reset
        </button>
      </div>

      {!gameWon ? (
        <div className="game-content">
          <div className="puzzle-display">
            <Calculator size={40} color="#3498db" />
            <h2 className="puzzle-question">{currentPuzzle.question}</h2>
          </div>

          <form onSubmit={handleSubmit} className="answer-form">
            <input
              type="number"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Enter your answer..."
              className="answer-input"
              autoFocus
            />
            
            <button type="submit" className="submit-button hover-lift">
              Check Answer
            </button>
          </form>

          {feedback && (
            <div className={`feedback ${feedback.includes('✅') ? 'correct' : feedback.includes('❌') ? 'incorrect' : 'warning'}`}>
              {feedback}
            </div>
          )}
        </div>
      ) : (
        <div className="game-won-overlay">
          <div className="game-won-modal animate-scaleIn">
            <Trophy size={60} color="#f39c12" />
            <h2>🎉 Game Complete!</h2>
            <p>You scored {score} out of {totalQuestions}!</p>
            <div className="win-stats">
              <div className="win-stat">
                <Star size={20} color="#f39c12" />
                <span>Accuracy: {Math.round((score / totalQuestions) * 100)}%</span>
              </div>
              <div className="win-stat">
                <Star size={20} color="#f39c12" />
                <span>Time: {formatTime(timer)}</span>
              </div>
            </div>
            <button 
              className="play-again-button hover-lift"
              onClick={handleReset}
            >
              Play Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MathPuzzle;
